import { useEffect, useRef, useState } from "react";
import { runEventsUrl } from "../api";
import { clockTime, fmtSigned, fmtUnits } from "../format";
import type { RunEvent } from "../types";

const TOOL_LABEL: Record<string, string> = {
  get_demand_data: "Checked the scope",
  get_sku_hierarchy: "Read the product hierarchy",
  get_monthly_actuals: "Read historical actuals",
  get_forecast_months: "Read the forecast",
  get_actuals_summary: "Summarised actuals trends",
  get_consensus_vs_budget: "Compared consensus with budget",
  write_forecast_adjustments: "Wrote forecast adjustments",
  write_actuals_adjustment: "Wrote history corrections",
  write_insights: "Recorded findings",
  finish_task: "Finished",
};

/** Live events while a run is active (SSE, resumes after a drop); stored events afterwards. */
export function useRunEvents(runId: string | null, live: boolean, stored: RunEvent[] | undefined, onDone: () => void) {
  const [events, setEvents] = useState<RunEvent[]>(stored ?? []);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!runId || !live) {
      setEvents(stored ?? []);
      return;
    }
    setEvents([]);
    const source = new EventSource(runEventsUrl(runId), { withCredentials: true });
    source.addEventListener("progress", (e) => {
      const ev = JSON.parse((e as MessageEvent).data) as RunEvent;
      setEvents((cur) => (cur.some((c) => c.seq === ev.seq) ? cur : [...cur, ev]));
    });
    source.addEventListener("end", () => {
      source.close();
      doneRef.current();
    });
    return () => source.close();
  }, [runId, live]); // eslint-disable-line react-hooks/exhaustive-deps

  return events;
}

interface Item { key: string; kind: "info" | "think" | "tool" | "write" | "err" | "done"; icon: string; title: string; detail?: string; ts?: string }

function argsText(args: Record<string, unknown> | undefined): string {
  if (!args) return "";
  const parts: string[] = [];
  for (const [k, v] of Object.entries(args)) {
    if (k === "filter" || v == null || v === "") continue;
    if (k === "adjustments" && typeof v === "object") {
      const a = v as { rows?: number; skus?: number; qty_total?: number };
      parts.push(`${a.rows} rows · ${a.skus} SKUs${a.qty_total != null ? ` · net ${fmtSigned(a.qty_total)}` : ""}`);
    } else if (Array.isArray(v)) parts.push(`${k}: ${v.join(", ")}`);
    else if (typeof v === "object") parts.push(k);
    else parts.push(`${k}: ${v}`);
  }
  return parts.join(" · ");
}

function toItems(events: RunEvent[]): Item[] {
  const items: Item[] = [];
  for (let n = 0; n < events.length; n++) {
    const e = events[n];
    const ts = e.ts;
    switch (e.type) {
      case "queued": items.push({ key: `${e.seq}`, kind: "info", icon: "•", title: `Queued, target scenario “${e.scenario}”`, ts }); break;
      case "loading_data": items.push({ key: `${e.seq}`, kind: "info", icon: "↧", title: "Loading data from Kinaxis", ts }); break;
      case "scope": items.push({ key: `${e.seq}`, kind: "info", icon: "◎", title: `${e.sku_count} SKUs in scope`, detail: String(e.scope), ts }); break;
      case "intent_classifying": items.push({ key: `${e.seq}`, kind: "info", icon: "◇", title: "Working out the type of work and scope", ts }); break;
      case "intent_classified": items.push({
        key: `${e.seq}`, kind: "info", icon: "◆",
        title: `Classified as ${e.label} (${e.confidence} confidence)`,
        detail: [String(e.rationale ?? ""), `scope: ${e.scope}`, e.method === "llm" ? "by the model" : "by keyword rules",
                 e.error ? `model unavailable: ${e.error}` : ""].filter(Boolean).join(" · "),
        ts,
      }); break;
      case "llm_text": items.push({ key: `${e.seq}`, kind: "think", icon: "✎", title: String(e.text), ts }); break;
      case "cancel_requested": items.push({ key: `${e.seq}`, kind: "err", icon: "■", title: "Cancel requested — stops at the next step", ts }); break;
      case "tool_call": {
        const tool = String(e.tool);
        if (tool === "finish_task") break;
        const next = events[n + 1];
        const result = next?.type === "tool_result" && next.tool === tool ? (next.result as Record<string, unknown>) : undefined;
        if (result) n++;
        const failed = result && (result.status === "rejected" || result.status === "failed" || result.error || result.errors);
        const isWrite = tool.startsWith("write_");
        let detail = argsText(e.args as Record<string, unknown>);
        if (failed) {
          const errs = (result!.errors as string[] | undefined) ?? [String(result!.error ?? "")];
          detail = errs.join(" · ");
        } else if (result && isWrite && result.records_written != null) {
          detail = `${fmtUnits(result.records_written as number)} rows written · net ${fmtSigned(result.qty_total as number)}`;
        } else if (result?.warning) {
          detail = `${detail ? `${detail} · ` : ""}${result.warning}`;
        }
        items.push({
          key: `${e.seq}`, kind: failed ? "err" : isWrite ? "write" : "tool", icon: failed ? "!" : isWrite ? "✓" : "→",
          title: TOOL_LABEL[tool] ?? tool, detail, ts,
        });
        break;
      }
      case "done": items.push({ key: `${e.seq}`, kind: "done", icon: "■", title: `Run ended: ${e.status}`, ts }); break;
      default: break;
    }
  }
  return items;
}

export default function RunActivity({ events, live }: { events: RunEvent[]; live: boolean }) {
  const items = toItems(events);
  const turn = [...events].reverse().find((e) => e.type === "turn");
  return (
    <div>
      {live && (
        <div className="row small ink2" style={{ marginBottom: 8 }}>
          <div className="spinner" /> Working{turn ? ` · step ${turn.turn} of at most ${turn.max_turns}` : ""}…
        </div>
      )}
      {items.length === 0 ? (
        <div className="muted small">{live ? "Waiting for the first step…" : "No activity recorded."}</div>
      ) : (
        <div className="log">
          {items.map((it) => (
            <div key={it.key} className={`log-item ${it.kind}`}>
              <span className="log-icon" aria-hidden>{it.icon}</span>
              <div style={{ minWidth: 0 }}>
                <div className="row" style={{ alignItems: "baseline" }}>
                  <span className="grow" style={{ whiteSpace: it.kind === "think" ? "pre-wrap" : undefined }}>{it.title}</span>
                  <span className="log-time">{clockTime(it.ts)}</span>
                </div>
                {it.detail && <div className="small muted">{it.detail}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
